import React, { useEffect, useState } from "react";
import api from "API/api";
import { toast } from "sonner";
import { DataGrid } from "@mui/x-data-grid";
import colors from "assets/theme/base/colors";
import { useLocation } from "react-router-dom";
import FilterListIcon from "@mui/icons-material/FilterList";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DatePicker from "react-datepicker";
import ClearIcon from "@mui/icons-material/Clear";
import useAnalyticsStore from "store/analyticsStore";
import { Pagination } from "../../../components/Common/pagination";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";

const ScenarioDetail = () => {
  const location = useLocation();
  const [page, setPage] = useState(0);
  const { white, gradients } = colors;
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [violationTypes, setViolationTypes] = useState([]);
  const [violationLoading, setViolationLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedOption, setSelectedOption] = useState("24hours");
  const { scenario, device, admin_site_id, scenario_id } =
    location?.state || {};

  const {
    paginationUrls,
    setPaginationUrls,
    scenarioViolation,
    setScenarioViolation,
  } = useAnalyticsStore();

  const handleSelectChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const [detectionStatus, setDetectionStatus] = useState(() => {
    return (
      localStorage.getItem(`${scenario_id}_detectionStatus`) ||
      location?.state?.scenario.status
    );
  });

  const handleDateRangeChange = (update) => {
    if (update.length === 2) {
      setStartDate(update[0]);
      setEndDate(update[1]);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const offset = d.getTimezoneOffset();
    d.setMinutes(d.getMinutes() - offset);
    return d.toISOString().split("T")[0];
  };

  const getAllViolations = async (url = null) => {
    try {
      setViolationLoading(true);
      let apiUrl = `/violations?site_id=${admin_site_id}&violation_type=${scenarioViolation}&scenario_id=${scenario_id}`;
      if (selectedOption !== "all") {
        if (selectedOption === "custom") {
          const formattedStart = formatDate(startDate);
          const formattedEnd = formatDate(endDate);
          apiUrl += `&start_date=${formattedStart}&end_date=${formattedEnd}`;
        } else {
          apiUrl += `&time_range=${selectedOption}`;
        }
      }

      const res = await api.get(url || apiUrl);
      if (res?.data?.results?.data?.length === 0) {
        toast.info("No Data Available For Selected Time Range");
      }

      const results = res?.data?.results?.data || [];
      setPaginationUrls({
        next: res?.data?.next,
        prev: res?.data?.previous,
      });
      const types = new Set();
      let flatRecords = [];
      results.forEach((service) => {
        service.violations.forEach((violation) => {
          types.add(violation.violation);
          flatRecords = flatRecords.concat(
            violation.records.filter((record) =>
              scenarioViolation ? record.violation === scenarioViolation : true
            )
          );
        });
      });

      setViolationTypes([...types]);
      setRecords(flatRecords);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to fetch violations"
      );
    } finally {
      setViolationLoading(false);
    }
  };

  useEffect(() => {
    getAllViolations();
  }, [scenarioViolation, selectedOption, startDate, endDate]);

  useEffect(() => {
    if (!scenario || !device) return;

    const SOCKET_URL = `${process.env.REACT_APP_SOCKET_URL}/${admin_site_id}/${scenario?.service_id}/`;
    const ws = new WebSocket(SOCKET_URL);

    ws.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      if (data?.value?.data) {
        const newRecord = data.value.data;

        setRecords((prevRecords) => {
          const updated = [newRecord, ...prevRecords];
          if (updated.length > 10) {
            updated.pop();
          }
          return updated;
        });
      }

      if (data?.value?.message) {
        toast.success(data?.value?.message);
      }

      if (data.value?.status === "request_consumed") {
        setLoading(false);
        setDetectionStatus("active");
        localStorage.setItem(`${scenario_id}_detectionStatus`, "active");
      }

      if (data?.value?.status === "completed") {
        setLoading(false);
        setDetectionStatus("inactive");
        localStorage.setItem(`${scenario_id}_detectionStatus`, "inactive");
      }
    };

    ws.onerror = () =>
      (ws.onclose = () => {
        setLoading(false);
      });

    setSocket(ws);
    return () => ws.close();
  }, [scenario, device]);

  const handleStartDetection = () => {
    setLoading(true);
    setDetectionStatus("starting");
    localStorage.setItem(`${scenario_id}_detectionStatus`, "starting");
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ status: "start" }));
    }
    setTimeout(() => {
      setLoading(false);
    }, 4000);
  };

  const handleStopDetection = () => {
    setLoading(true);
    setDetectionStatus("inactive");
    localStorage.setItem(`${scenario_id}_detectionStatus`, "inactive");
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ status: "stop" }));
    }
    setTimeout(() => {
      setLoading(false);
    }, 4000);
  };

  const filteredRecords = records.filter((record) =>
    scenarioViolation ? record.violation === scenarioViolation : true
  );

  const columns = [
    { field: "violation", headerName: "Violation", minWidth: 180, flex: 1 },
    { field: "description", headerName: "Description", minWidth: 400, flex: 1 },
    { field: "area", headerName: "Area", minWidth: 150, flex: 1 },
    { field: "device", headerName: "Device", minWidth: 240, flex: 1 },
    { field: "created_at", headerName: "Created At", minWidth: 240, flex: 1 },
  ];

  const handleNextPage = () => {
    if (paginationUrls?.next) {
      setPage((prev) => prev + 1);
      getAllViolations(paginationUrls?.next);
    }
  };

  const handlePrevPage = () => {
    if (paginationUrls?.prev && page > 0) {
      setPage((prev) => prev - 1);
      getAllViolations(paginationUrls?.prev);
    }
  };
  const NoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        fontSize: "20px",
        fontWeight: 500,
        color: "#666",
        opacity: "0.4",
      }}
    >
      No Data Found
    </Box>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
          className="filter-div"
        >
          {violationTypes?.length > 0 && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              className="filter-box"
            >
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                position="relative"
              >
                <IconButton>
                  <FilterListIcon />
                </IconButton>
                <Select
                  value={scenarioViolation}
                  onChange={(e) => setScenarioViolation(e.target.value)}
                  displayEmpty
                  sx={{ ml: 1 }}
                  className="react-select violation-select"
                >
                  <MenuItem value="">All Violations</MenuItem>

                  {violationTypes.map((type, index) => (
                    <MenuItem key={index} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {scenarioViolation && (
                  <IconButton
                    onClick={() => setScenarioViolation("")}
                    size="small"
                    sx={{ position: "absolute", right: "20px" }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}
          <Box
            sx={{ marginRight: "16px", display: "flex", marginLeft: "auto" }}
            className="select-box"
          >
            <FormControl>
              <InputLabel>Select Time Range</InputLabel>
              <Select
                value={selectedOption || "24hours"}
                onChange={handleSelectChange}
                className="react-select analytics-select"
                sx={{
                  background: "white",
                  marginRight: "0 !important",
                  width: "200px",
                }}
                label="Select Time Range"
              >
                <MenuItem selected value="24hours">
                  24 Hours
                </MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="48hours">48 Hours</MenuItem>
                <MenuItem value="72hours">72 Hours</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>

            {selectedOption === "custom" && (
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                isClearable={false}
                customInput={
                  <Button
                    sx={{
                      border: "none",
                      background: "#063863",
                      color: "white !important",
                      height: "42px",
                      width: "180px",
                      borderRadius: "6px",
                      marginBottom: "28px",
                      marginLeft: "7px",
                      "&:hover": {
                        background: "#063863",
                        color: "white !important",
                      },
                    }}
                  >
                    Select Date Range
                  </Button>
                }
              />
            )}

            <Box>
              <Button
                className="start-btn"
                onClick={
                  detectionStatus === "inactive"
                    ? handleStartDetection
                    : handleStopDetection
                }
                disabled={loading}
                sx={{
                  height: "38px",
                  width: "157px",
                  fontSize: "12px",
                  fontWeight: "500",
                  marginLeft: "5px",
                  borderRadius: "6px",
                  background: `${gradients.info.main}`,
                  color: white.main,
                  border: "none",
                  marginRight: "0",
                  marginBottom: "0",
                  "&:hover": {
                    background: `${gradients.info.state} !important`,
                    color: white.main,
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size="14px" />
                ) : detectionStatus === "inactive" ? (
                  "Start Detection"
                ) : (
                  "Stop Detection"
                )}
              </Button>
            </Box>
          </Box>
        </Box>

        <Box>
          <DataGrid
            hideFooter
            pageSize={10}
            columns={columns}
            loading={violationLoading}
            disableRowSelectionOnClick
            rows={filteredRecords.map((record) => ({
              id: record.id,
              violation: record.violation,
              created_at: record.created_at,
              description: record.description,
              area: record.area,
              device: record.device,
            }))}
            slots={{
              noRowsOverlay: NoRowsOverlay,
            }}
            sx={{
              boxShadow: 1,
              fontSize: "14px",
              borderRadius: "8px",
              "& .MuiDataGrid-overlayWrapperInner": { background: "white" },
              "& .MuiCircularProgress-root": {
                color: `${gradients.info.main}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#fff",
                fontWeight: "500",
              },
              "& .MuiDataGrid-cell": {
                outlineOffset: "none !important",
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader": {
                background: "#063863",
                "& .MuiSvgIcon-root": { color: "#fff !important" },
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(odd)": { backgroundColor: "#f8f9fa" },
                "&:hover": {
                  backgroundColor: "rgb(25 108 215 / 0.02) !important",
                },
              },
            }}
          />
          {records.length > 0 && (
            <Pagination
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              paginationUrls={paginationUrls}
            />
          )}
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default ScenarioDetail;
